import { pathOr, prop, path, propOr, prepend } from 'ramda';
import { retry, handleWhen, circuitBreaker, ConsecutiveBreaker, ExponentialBackoff, wrap } from 'cockatiel';

const isRunningInBrowser = false;

let _trackJS;

const trackJS = () => _trackJS;

const setTrackJS = track => {
    if (_trackJS !== track) _trackJS = track;
};

const banner = message => {
    if (!isRunningInBrowser) return; // browser only; not tests

    // eslint-disable-next-line no-console
    console.warn(
        `********************************************************************************************************** 
*       
*   ${message}
*   You are using mock-data!
*       
**********************************************************************************************************`,
    );
};

const readJson = async relativePath => {
    if (isRunningInBrowser) {
        try {
            const response = await fetch(`/mock-data/${relativePath}`);
            return response.json();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Unable to load /mock-data/${relativePath}`);
            throw err;
        }
    } else {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const fs = require('fs');
        const path = fs.existsSync(relativePath) ? relativePath : `${__dirname}/../../mock-data/${relativePath}`;

        const string = fs.readFileSync(path, { encoding: 'utf8' });
        return Promise.resolve(JSON.parse(string));
    }
};

/*
 * Return the embedded response.body.data if the response has a valid statusCode or throw an error
 * Bear in mind that the original call could *also* have rejected (though the exact semantics of why is hazy)
 * @sig checkResponseAndGetData :: HtmlResponse -> *
 */
const checkResponse = response => {
    if (response.statusCode > 199 && response.statusCode < 300) return response;
    throw new Error(pathOr('An unknown error occurred', ['body', 'errors', '0', 'detail'], response));
};

const checkStatus = response => {
    if (response.status > 199 && response.status < 300) return response;
    throw new Error(pathOr('An unknown error occurred', ['body'], response));
};

const axiosData = response => {
    checkStatus(response);
    return prop('data', response);
};

// :: HtmlResponse -> HtmlResponseBody
const body = response => {
    checkResponse(response);
    return path(['body'], response);
};

// :: HtmlResponse -> *
const data = response => {
    checkResponse(response);
    return path(['body', 'data'], response);
};

const id = response => {
    checkResponse(response);
    return path(['body', 'id'], response);
};

const responseUrl = response => {
    checkResponse(response);
    return path(['xhr', 'responseURL'], response);
};

const logErrorToTrackJS = error => {
    if (!trackJS()) return;

    trackJS().console.log({
        requestId: pathOr('', ['response', 'body', 'meta', 'requestId'], error),
        method: pathOr('', ['response', 'body', 'meta', 'method'], error),
        status: prop('status', error),
        detail: pathOr('', ['response', 'body', 'errors', '0', 'detail'], error),
    });

    // Record the Error
    trackJS().track(error);
};

/*
 * Return the embedded error.response.body.errors if the call rejected
 * @sig failure :: Error -> *
 */
const failure = error => {
    const details = pathOr('An unknown error occurred', ['response', 'body', 'errors', '0'], error);
    const method = pathOr('An unknown method called', ['response', 'body', 'meta', 'method'], error);
    const message = propOr('', 'message', error);
    // eslint-disable-next-line no-console
    console.error(message || details);

    logErrorToTrackJS(error);
    return { details, method };
};

const parseAiJson = response => JSON.parse(response.text);

const retryPolicy = retry(
    handleWhen(error => error.status === 503),
    {
        maxAttempts: 4,
        backoff: new ExponentialBackoff({
            initialDelay: 5000,
            maxDelay: 20000,
            factor: 2,
        }),
    },
);

const circuitBreakerPolicy = circuitBreaker(
    handleWhen(error => error.status === 503),
    {
        halfOpenAfter: 10 * 1000, // Try again after 10 seconds once tripped
        breaker: new ConsecutiveBreaker(5), // Trip the breaker after 5 consecutive failures
    },
);

const retryWithBreaker = wrap(retryPolicy, circuitBreakerPolicy);

/*
 * Create a function that can make an async call to a remote function of some client.
 *
 * When the returned function is called, it will, in turn, call the remote client and resolve or reject
 * by calling the functions passed into makeRequest.
 *
 * The parameters of the created function will vary depending on what the client function needs.
 *
 * resolve wll be sent the client function's response along with the arguments passed to the client
 * (venueDetails, above, shows an example where the arg "venueId" is passed along with the response from the client)
 *
 * Likewise, reject will be sent the error along with the arguments
 *
 * The default resolve is data; the default reject is failure
 *
 * makeRequest :: (() -> Client, String, Resolve, Reject) -> RemoteCall
 *
 *   Resolve = (HttpResponse, ...*) -> *
 *   Reject = (Error, ...*) -> *
 *   RemoteCall = ...* -> Promise *
 */

const makeRequestWithPolicy =
    (getClient, functionName, policy, resolve = data, reject = failure) =>
    async (...args) => {
        try {
            const response = await policy.execute(async () => {
                const client = await getClient();
                return await client[functionName](...args);
            });
            const responsePlusArgs = prepend(response, args);
            return resolve(...responsePlusArgs);
        } catch (err) {
            const errorPlusArgs = prepend(err, args);
            return reject(...errorPlusArgs);
        }
    };

const makeRequest =
    (getClient, functionName, resolve = data, reject = failure) =>
    async (...args) => {
        try {
            const client = await getClient();
            const response = await client[functionName](...args);
            const responsePlusArgs = prepend(response, args);
            return resolve(...responsePlusArgs);
        } catch (err) {
            const errorPlusArgs = prepend(err, args);
            reject(...errorPlusArgs);
        }
    };

export {
    body,
    data,
    id,
    responseUrl,
    failure,
    parseAiJson,
    setTrackJS,
    makeRequest,
    banner,
    readJson,
    axiosData,
    retryWithBreaker,
    makeRequestWithPolicy,
};
